import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, FileImage, FileSpreadsheet, File } from 'lucide-react';
import { Document } from '@/types';
import { DocumentsService } from '@/services/documentsService';

interface UseDocumentsProps {
  tramiteId: number;
}

export const useDocuments = ({ tramiteId }: UseDocumentsProps) => {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  // Query para obtener documentos
  const {
    data: documents = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['documents', tramiteId],
    queryFn: () => DocumentsService.getDocuments(tramiteId),
    enabled: !!tramiteId,
    refetchOnWindowFocus: false,
  });

  // Mutación para subir archivos
  const uploadFilesMutation = useMutation({
    mutationFn: async (files: FileList) => {
      const uploadedDocuments: Document[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validation = validateFile(file);

        if (!validation.valid) {
          throw new Error(validation.error);
        }

        const uploadedDocument = await DocumentsService.uploadDocument(tramiteId, file);
        uploadedDocuments.push(uploadedDocument);
      }

      return uploadedDocuments;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', tramiteId] });
    }
  });

  // Mutación para eliminar documento
  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: number) => DocumentsService.deleteDocument(tramiteId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', tramiteId] });
    }
  });

  // Mutación para actualizar nombre de documento
  const updateDocumentNameMutation = useMutation({
    mutationFn: ({ documentId, name }: { documentId: number; name: string }) => 
      DocumentsService.updateDocumentName(tramiteId, documentId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', tramiteId] });
    }
  });

  // Subir archivos
  const uploadFiles = useCallback(async (files: FileList) => {
    return uploadFilesMutation.mutateAsync(files);
  }, [uploadFilesMutation]);

  // Eliminar documento
  const deleteDocument = useCallback(async (documentId: number) => {
    return deleteDocumentMutation.mutateAsync(documentId);
  }, [deleteDocumentMutation]);

  // Actualizar nombre de documento
  const updateDocumentName = useCallback(async (documentId: number, name: string) => {
    return updateDocumentNameMutation.mutateAsync({ documentId, name });
  }, [updateDocumentNameMutation]);

  // Descargar documento
  const downloadDocument = useCallback(async (document: Document) => {
    try {
      const blob = await DocumentsService.downloadDocument(tramiteId, document.id);

      // Crear un enlace temporal para descargar el archivo
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.name;
      link.click();

      // Limpiar el URL temporal
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  }, [tramiteId]);

  // Formatear tamaño de archivo
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Obtener icono de archivo
  const getFileIcon = useCallback((type: string) => {
    if (type.includes('pdf')) return FileText;
    if (type.includes('word') || type.includes('document')) return FileText;
    if (type.includes('image')) return FileImage;
    if (type.includes('excel') || type.includes('spreadsheet')) return FileSpreadsheet;
    return File;
  }, []);

  // Obtener etiqueta de tipo de archivo
  const getFileTypeLabel = useCallback((type: string) => {
    if (type.includes('pdf')) return 'PDF';
    if (type.includes('word') || type.includes('document')) return 'Word';
    if (type.includes('image')) return 'Imagen';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'Excel';
    return 'Archivo';
  }, []);

  // Validar archivo
  const validateFile = useCallback((file: File): { valid: boolean; error?: string } => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif'
    ];

    if (file.size > maxSize) {
      return { valid: false, error: 'El archivo es muy grande. Máximo 10MB permitido.' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Tipo de archivo no permitido.' };
    }

    return { valid: true };
  }, []);

  return {
    // Data
    documents,
    isLoading,
    error,
    totalDocuments: documents.length,

    // Actions
    uploadFiles,
    deleteDocument,
    updateDocumentName,
    downloadDocument,
    refetch,

    // Mutation states
    isUploading: uploadFilesMutation.isPending,
    isDeleting: deleteDocumentMutation.isPending,
    isUpdating: updateDocumentNameMutation.isPending,

    // Helper functions
    formatFileSize,
    getFileIcon,
    getFileTypeLabel,
    validateFile,
  };
};

export default useDocuments;
